import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { isFieldForListingType } from '../../util/fieldHelpers';
import { Heading } from '../../components';
import css from './ListingPage.module.css';
import { formatMaybeNumber, removeExactDuplicates } from '../../util/genericHelpers';

// ✅ IMPORT THAI LABELS
import { FIELD_HEADING_TH, OPTION_LABEL_TH } from '../../util/thaiFields';

const isThaiLocale = (intl, locale) => {
  const loc = String(locale || intl?.locale || '').toLowerCase();
  return loc === 'th' || loc.startsWith('th-');
};

const fallbackTitleCase = key =>
  String(key || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());

const translateLabel = ({ normalizedKey, defaultLabel, intl, locale }) => {
  if (!isThaiLocale(intl, locale)) return defaultLabel;
  return FIELD_HEADING_TH?.[normalizedKey] || defaultLabel;
};

const translateOptionValue = ({ value, intl, locale }) => {
  if (!isThaiLocale(intl, locale)) return value;
  return OPTION_LABEL_TH?.[value] || value;
};

const translateEnumOptions = ({ enumOptions, intl, locale }) => {
  if (!isThaiLocale(intl, locale) || !Array.isArray(enumOptions)) return enumOptions;
  return enumOptions.map(o => ({
    ...o,
    label: OPTION_LABEL_TH?.[o.option] || o.label,
  }));
};

const SectionDetailsMaybe = props => {
  const {
    publicData,
    metadata = {},
    listingFieldConfigs,
    isFieldForCategory,
    intl,
    locale, // ✅ pass locale from parent (same as you did in CustomListingFields)
  } = props;

  if (!publicData || !listingFieldConfigs) return null;

  const generalInfoKeys = [
    'theme',
    'pub_theme',
    'property_size_m2',
    'ceiling_height',
    'pub_ceiling_height',
    'power_supply',
    'pub_power_supply',
    'safety_and_security',
    'pub_safety_and_security',
    'number_bedrooms',
    'number_bathrooms',
    'main_floor_number',
  ];

  const parkingInfoKeys = [
    'parking_spots',
    'parking_space',
    'loading_dock',
  ];

  const normalizeKey = key => key.replace(/^pub_/, '');

  const enrichedGeneralInfoConfigs = generalInfoKeys
    .map(key => {
      const pubKey = key.startsWith('pub_') ? key : `pub_${key}`;
      const baseKey = normalizeKey(key);
      const config =
        listingFieldConfigs.find(
          c => c.key === key || c.key === pubKey || normalizeKey(c.key) === baseKey
        ) || {};

      const value = publicData[key] ?? publicData[pubKey] ?? config.value;
      if (value == null) return null;

      const defaultLabel = config.showConfig?.label || fallbackTitleCase(baseKey);

      const localizedLabel = translateLabel({
        normalizedKey: baseKey,
        defaultLabel,
        intl,
        locale,
      });

      const enumOptions = translateEnumOptions({
        enumOptions: config.enumOptions || [],
        intl,
        locale,
      });

      return {
        key: config.key || pubKey,
        normalizedKey: baseKey,
        schemaType:
          config.schemaType ||
          (Array.isArray(value) ? 'multi-enum' : typeof value === 'boolean' ? 'boolean' : 'long'),
        showConfig: {
          isDetail: true,
          label: localizedLabel,
        },
        enumOptions,
        categoryConfig: config.categoryConfig || { categoryIds: ['studio', 'studio_videography'] },
        value,
      };
    })
    .filter(Boolean);

  const enrichedKeys = new Set(enrichedGeneralInfoConfigs.map(cfg => cfg.normalizedKey));
  const allListingFieldConfigs = [
    ...enrichedGeneralInfoConfigs,
    ...listingFieldConfigs.filter(config => {
      const normKey = normalizeKey(config.key);
      return !enrichedKeys.has(normKey);
    }),
  ];

  const isNumericValue = val =>
    (typeof val === 'number' && !Number.isNaN(val)) ||
    (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val)));

  const formatNumberSafe = val => intl.formatNumber(Number(val), { maximumFractionDigits: 0 });

  const pickListingFields = (acc, config) => {
    const { key, schemaType, value, showConfig = {}, enumOptions = [] } = config;
    const { isDetail, label } = showConfig;
    const listingType = publicData.listingType;
    const normalizedKey = normalizeKey(key);
    const isValid =
      isDetail &&
      isFieldForListingType(listingType, config) &&
      isFieldForCategory(config) &&
      value != null;

    if (!isValid) return acc;

    const findLabel = val => {
      const match = enumOptions.find(o => o.option === val);
      return match ? match.label : val;
    };

    let displayValue = value;

    if (schemaType === 'multi-enum' && Array.isArray(value)) {
      displayValue = value.map(findLabel).join(', ');
    } else if (schemaType === 'enum') {
      displayValue = findLabel(value);
    } else if (schemaType === 'boolean') {
      displayValue = value
        ? intl.formatMessage({ id: 'SearchPage.detailYes' })
        : intl.formatMessage({ id: 'SearchPage.detailNo' });
    }

    displayValue = formatMaybeNumber(intl, normalizedKey, displayValue);

    return [...acc, { key, label, value: displayValue }];
  };

  const displayFields = allListingFieldConfigs.reduce(pickListingFields, []);
  return displayFields.length > 0 ? (
    <div>
      <section className={css.sectionDetails}>
        <Heading as="h2" rootClassName={css.sectionHeading}>
          <FormattedMessage id="ListingPage.detailsTitle" />
        </Heading>

        <ul className={css.details}>
          {removeExactDuplicates(displayFields).map(detail => (
            <li key={detail.key} className={css.detailsRow}>
              <span className={css.detailLabel}>{detail.label}: </span>
              <span>{detail.value || 'N/A'}</span>
            </li>
          ))}
        </ul>
      </section>
      <section className={css.sectionDetails2}>
        <Heading as="h2" rootClassName={css.sectionHeading}>
          <FormattedMessage id="ListingPage.ParkingTitle" />
        </Heading>

        <ul className={css.details}>
          {allListingFieldConfigs
            .filter(cfg =>
              ['parking_spots', 'parking_space', 'loading_dock'].includes(
                cfg.key.replace(/^pub_/, '')
              )
            )
            .map(cfg => {
              const rawValue =
                publicData[cfg.key] ?? publicData[cfg.key.replace(/^pub_/, '')];

              if (rawValue == null) return null;

              let displayValue = rawValue;

              if (cfg.schemaType === 'enum') {
                const opt = cfg.enumOptions?.find(o => o.option === rawValue);
                displayValue = opt?.label || rawValue;
              }

              if (cfg.schemaType === 'multi-enum' && Array.isArray(rawValue)) {
                displayValue = rawValue
                  .map(v => {
                    const opt = cfg.enumOptions?.find(o => o.option === v);
                    return opt?.label || v;
                  })
                  .join(', ');
              }

              displayValue = formatMaybeNumber(
                intl,
                cfg.key.replace(/^pub_/, ''),
                displayValue
              );

              return (
                <li key={cfg.key} className={css.detailsRow}>
                  <span className={css.detailLabel}>
                    {cfg.showConfig?.label || cfg.label}:
                  </span>
                  <span>{' ' + (displayValue || 'N/A')}</span>
                </li>
              );
            })}
        </ul>
      </section>
    </div>
  ) : null;
};

export default SectionDetailsMaybe;
